import { X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { IconButton } from './button';

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

export interface DialogContentProps {
  title: ReactNode;
  description?: ReactNode;
  /** Accessible label for the close button (translated by the caller). */
  closeLabel: string;
  children?: ReactNode;
  footer?: ReactNode;
  /** Wide dialogs (the new-position form) are 640 px instead of 560 px. */
  wide?: boolean;
  /** Makes the dialog a form: Enter in a field and ⌘↵ submit. */
  onSubmit?: () => void;
  /** Call `event.preventDefault()` to keep the dialog open (e.g. while Esc cancels a drag). */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
}

export function DialogContent({
  title,
  description,
  closeLabel,
  children,
  footer,
  wide = false,
  onSubmit,
  onEscapeKeyDown,
}: DialogContentProps) {
  const body = (
    <>
      <header className="flex items-start justify-between gap-4 px-4 pt-3.5 pb-2">
        <div className="min-w-0">
          <RadixDialog.Title className="text-base font-medium">{title}</RadixDialog.Title>
          {description !== undefined && (
            <RadixDialog.Description className="mt-0.5 text-sm text-text-3">
              {description}
            </RadixDialog.Description>
          )}
        </div>
        <RadixDialog.Close asChild>
          <IconButton label={closeLabel} size="sm">
            <X />
          </IconButton>
        </RadixDialog.Close>
      </header>
      {children !== undefined && <div className="overflow-y-auto px-4 pb-4">{children}</div>}
      {footer !== undefined && (
        <footer className="flex justify-end gap-2 border-t border-border-subtle px-4 py-2.5">
          {footer}
        </footer>
      )}
    </>
  );
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
      <RadixDialog.Content
        className={cn(
          'fixed top-[18vh] left-1/2 z-50 flex max-h-[70vh] -translate-x-1/2 animate-scale-in flex-col',
          wide ? 'w-[min(640px,calc(100vw-32px))]' : 'w-[min(560px,calc(100vw-32px))]',
          'rounded-lg bg-bg-2 text-text-1 shadow-popover outline-none',
        )}
        {...(description === undefined ? { 'aria-describedby': undefined } : {})}
        {...(onEscapeKeyDown ? { onEscapeKeyDown } : {})}
        onOpenAutoFocus={(event) => {
          const field = (event.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>(
            'input:not([readonly]), textarea, button[role="combobox"]',
          );
          if (field) {
            event.preventDefault();
            field.focus();
          }
        }}
        onKeyDown={(event) => {
          if (onSubmit && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            onSubmit();
          }
        }}
      >
        {onSubmit ? (
          <form
            noValidate
            className="flex min-h-0 flex-col"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            {body}
          </form>
        ) : (
          body
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
